/**
 * LocalStorage-Abstraktion für „Meine Veranstaltungen".
 *
 * Der Browser ist die einzige Quelle der Wahrheit für diese Liste — wir
 * synchronisieren sie nie an den Server (das wäre ein Account).
 */

const STORAGE_KEY = "bm_recent";

export type RecentEvent = {
  shortcut: string;
  key: string;
  role: "admin" | "user";
  name: string;
  eventAt: string;
  lastOpened: string;
  /**
   * Zähler für aufeinanderfolgende Sync-Läufe, in denen das Event `exists: false`
   * geliefert hat. Ab 3 wird der Eintrag aus der Liste entfernt. Schützt vor
   * kurzen Netzwerkproblemen.
   */
  missCount?: number;
};

// Cached snapshot — `useSyncExternalStore` verlangt, dass `getSnapshot`
// stabile Identität liefert, solange die Daten unverändert sind. Wir cachen
// den rohen JSON-String und den geparsten Array und geben bei unverändertem
// String dieselbe Array-Referenz zurück.
let CACHED_RAW: string | null = null;
let CACHED_ENTRIES: RecentEvent[] = [];

function readAll(): RecentEvent[] {
  if (typeof window === "undefined") return CACHED_ENTRIES;
  const raw = window.localStorage.getItem(STORAGE_KEY) ?? "";
  if (raw === CACHED_RAW) return CACHED_ENTRIES;
  CACHED_RAW = raw;
  if (!raw) {
    CACHED_ENTRIES = [];
    return CACHED_ENTRIES;
  }
  try {
    const parsed = JSON.parse(raw) as unknown;
    CACHED_ENTRIES = Array.isArray(parsed) ? parsed.filter(isRecentEvent) : [];
  } catch {
    CACHED_ENTRIES = [];
  }
  return CACHED_ENTRIES;
}

function writeAll(entries: RecentEvent[]): void {
  const serialized = JSON.stringify(entries);
  window.localStorage.setItem(STORAGE_KEY, serialized);
  CACHED_RAW = serialized;
  CACHED_ENTRIES = entries;
}

function isRecentEvent(v: unknown): v is RecentEvent {
  return (
    typeof v === "object" &&
    v !== null &&
    typeof (v as RecentEvent).shortcut === "string" &&
    typeof (v as RecentEvent).key === "string" &&
    ((v as RecentEvent).role === "admin" || (v as RecentEvent).role === "user")
  );
}

export const recentEventsStorage = {
  getAll: readAll,

  /** Eintrag hinzufügen oder updaten (Schlüssel: shortcut + role). */
  upsert(entry: Omit<RecentEvent, "lastOpened" | "missCount">): RecentEvent[] {
    const current = readAll();
    const next: RecentEvent = {
      ...entry,
      lastOpened: new Date().toISOString(),
      missCount: 0,
    };
    const existingIdx = current.findIndex(
      (e) => e.shortcut === entry.shortcut && e.role === entry.role,
    );
    const updated =
      existingIdx >= 0
        ? current.map((e, i) => (i === existingIdx ? { ...e, ...next } : e))
        : [...current, next];
    writeAll(updated);
    return updated;
  },

  /** Eintrag entfernen (ein bestimmter oder alle Rollen zu einem Shortcut). */
  remove(shortcut: string, role?: "admin" | "user"): RecentEvent[] {
    const updated = readAll().filter(
      (e) => !(e.shortcut === shortcut && (role === undefined || e.role === role)),
    );
    writeAll(updated);
    return updated;
  },

  /** Bulk-Update nach Sync-Ergebnis. Nicht mehr gefundene Events zählen den
   * miss-Counter hoch; ab 3 Treffern fallen sie raus. */
  applySyncResults(
    results: Record<string, { exists: boolean; name?: string; eventAt?: string }>,
  ): RecentEvent[] {
    const current = readAll();
    const updated: RecentEvent[] = [];
    for (const entry of current) {
      const res = results[entry.shortcut];
      if (!res) {
        updated.push(entry);
        continue;
      }
      if (res.exists) {
        updated.push({
          ...entry,
          name: res.name ?? entry.name,
          eventAt: res.eventAt ?? entry.eventAt,
          missCount: 0,
        });
      } else {
        const miss = (entry.missCount ?? 0) + 1;
        if (miss < 3) {
          updated.push({ ...entry, missCount: miss });
        }
      }
    }
    writeAll(updated);
    return updated;
  },

  clear(): void {
    writeAll([]);
  },
};
